import { TENANTS } from '../config';

const STYLES = `
<style>
  .login-shell { min-height: 100vh; min-height: 100svh; min-height: 100dvh; display: flex; flex-direction: column; background: var(--bg); color: var(--ink); -webkit-font-smoothing: antialiased; }
  .login-header { display: flex; align-items: center; justify-content: center; padding: 20px 16px 4px; }
  .brand { display: inline-flex; align-items: center; gap: 10px; text-decoration: none; color: var(--ink); }
  .brand-mark { width: 36px; height: 36px; border-radius: 12px; background: linear-gradient(135deg, var(--accent), var(--accent-deep)); display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 10px 24px -10px var(--accent-glow); }
  .brand strong { font-family: 'Fraunces', serif; font-size: 24px; font-weight: 600; letter-spacing: -0.015em; color: var(--ink); }
  .login-main { flex: 1; display: grid; place-items: center; padding: 32px 20px; }
  .login-card { width: 100%; max-width: 380px; background: var(--bg-elev); border: 1px solid var(--rule); border-radius: 18px; padding: 28px 22px; box-shadow: 0 30px 60px -30px rgba(0,0,0,0.16); box-sizing: border-box; text-align: left; }
  .login-card .t-display { font-family: 'Fraunces', serif; font-weight: 600; font-size: 30px; letter-spacing: -0.02em; margin: 6px 0 8px; color: var(--ink); line-height: 1.1; }
  .login-sub { margin: 0 0 22px; color: var(--ink-2); font-size: 13.5px; line-height: 1.45; }
  .login-card label { display: flex; flex-direction: column; gap: 6px; font-size: 12px; font-weight: 600; color: var(--ink-2); letter-spacing: 0.04em; text-transform: uppercase; margin-bottom: 12px; }
  .login-card select, .login-card input { width: 100%; min-height: 46px; padding: 10px 12px; border-radius: 10px; background: var(--bg); border: 1px solid var(--rule); font: inherit; color: var(--ink); box-sizing: border-box; }
  .login-error { background: color-mix(in srgb, #d97706 18%, transparent); border: 1px solid color-mix(in srgb, #d97706 38%, transparent); color: var(--ink); padding: 10px 12px; border-radius: 10px; font-size: 13px; display: none; margin-bottom: 12px; }
  .login-error.show { display: block; }
  .login-success { background: color-mix(in srgb, #16a34a 18%, transparent); border: 1px solid color-mix(in srgb, #16a34a 38%, transparent); color: var(--ink); padding: 10px 12px; border-radius: 10px; font-size: 13px; display: none; margin-bottom: 12px; }
  .login-success.show { display: block; }
  .login-foot { color: var(--ink-3); font-size: 12px; margin-top: 20px; text-align: center; line-height: 1.45; }
  .login-foot code { font-family: 'JetBrains Mono', monospace; background: var(--bg-sunken); padding: 1px 6px; border-radius: 6px; font-size: 11px; }
  .google-btn { display: inline-flex; align-items: center; justify-content: center; gap: 12px; min-height: 48px; width: 100%; background: #fff; color: #1f2937; border: 1px solid #d1d5db; border-radius: 10px; font: inherit; font-weight: 600; font-size: 15px; cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
  .google-btn:hover { background: #f9fafb; }
  .google-btn svg { display: block; }
  .login-divider { display: flex; align-items: center; gap: 12px; color: var(--ink-3); font-size: 12px; margin: 18px 0; }
  .login-divider::before, .login-divider::after { content: ''; flex: 1; height: 1px; background: var(--rule); }
  .request-box { background: var(--bg-sunken); border: 1px solid var(--rule); border-radius: 14px; padding: 18px; margin-top: 8px; }
  .request-box .t-h3 { margin: 0 0 6px; font-size: 16px; font-weight: 600; }
  .request-box .t-meta { color: var(--ink-2); font-size: 13px; line-height: 1.5; margin-bottom: 14px; }
  .pill-warn-inv { background: color-mix(in srgb, #d97706 16%, transparent); color: #b45309; padding: 4px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; display: inline-block; margin-bottom: 12px; }
  @media (min-width: 720px) {
    .login-card { max-width: 440px; padding: 36px 32px; }
    .login-card .t-display { font-size: 38px; }
  }
</style>
`;

export function renderLogin(opts?: { inviteCode?: string; inviteRole?: string; inviteEmail?: string }): string {
  const tOpts = Object.values(TENANTS);
  const optsHtml = tOpts.map((t, i) => `<option value="${t.tenantId}" ${i === 0 ? 'selected' : ''}>${t.nameLatin}</option>`).join('');

  const inviteBanner = opts?.inviteCode
    ? `<div class="pill-warn-inv">You were invited as <strong>${opts.inviteRole || 'member'}</strong>${opts.inviteEmail ? ' (' + opts.inviteEmail + ')' : ''}. Sign in with Google to accept.</div>`
    : '';

  return STYLES + `
    <div class="login-shell">
      <header class="login-header">
        <a class="brand" href="#/login" aria-label="clientale">
          <span class="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
              <path d="M16 8.5C15.4 7.2 14.2 6.4 12.6 6.4c-2.8 0-4.9 2.1-4.9 4.9s2.1 4.9 4.9 4.9c1.5 0 2.8-.7 3.4-1.9" stroke="#fff" stroke-width="1.9" stroke-linecap="round"/>
            </svg>
          </span>
          <strong>clientale</strong>
        </a>
      </header>
      <main class="login-main">
        <section class="login-card" aria-labelledby="login-head">
          <div class="t-eyebrow">Workspace</div>
          <h1 id="login-head" class="t-display">Sign in to ${tOpts[0].nameLatin.toLowerCase()}.</h1>
          ${inviteBanner}
          <p class="login-sub">Tap below — Google handles the password, we handle the access list. If you're not on it yet, you can request access right here.</p>

          <div class="login-error" data-login-error role="alert"></div>
          <div class="login-success" data-login-success role="status"></div>

          <label><span>Workspace</span>
            <select name="tenant" data-login-tenant>${optsHtml}</select>
          </label>

          <button class="google-btn" type="button" data-action="google-signin" id="google-signin">
            <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.13 4.13 0 0 1-1.79 2.71v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.61z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.71H.96v2.33A9 9 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.95 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58z"/>
            </svg>
            <span data-google-btn-text>Sign in with Google</span>
          </button>

          <div class="login-divider">or</div>

          <div class="request-box" data-pane="request">
            <div class="t-h3">Not on the access list yet?</div>
            <div class="t-meta">Tap <strong>Request access</strong> after signing in with Google — the workspace owner gets a notification and approves you with one click.</div>
            <button class="btn btn-ghost" type="button" data-action="request-access" id="request-access-btn">
              Request access
            </button>
          </div>

          <p class="login-foot">By continuing you agree to your workspace owner's policies. Need help? Email the owner — your access request is the fastest route.</p>
        </section>
      </main>
    </div>
  `;
}

interface PendingUser { uid: string; email: string; name: string; }

async function getCurrentPendingUser(): Promise<PendingUser | null> {
  try {
    const { getFirebase } = await import('../firebase');
    const fb = await getFirebase();
    const u = fb?.auth?.currentUser;
    if (!u || !u.email) return null;
    return { uid: u.uid, email: u.email, name: u.displayName || u.email.split('@')[0] };
  } catch { return null; }
}

function setButton(btn: HTMLButtonElement | null, pendingUser: PendingUser | null) {
  if (!btn) return;
  if (pendingUser) {
    btn.disabled = false;
    btn.textContent = 'Request access as ' + pendingUser.email;
  } else {
    btn.disabled = true;
    btn.textContent = 'Sign in with Google first';
  }
}

export function bindLogin(): void {
  let pendingUser: PendingUser | null = null;

  const tenant = document.querySelector<HTMLSelectElement>('[data-login-tenant]');
  if (tenant) document.body.dataset.tenant = tenant.value;

  const setError = (msg: string) => {
    const el = document.querySelector<HTMLElement>('[data-login-error]');
    if (!el) return;
    el.textContent = msg; el.classList.add('show');
  };
  const setSuccess = (msg: string) => {
    const el = document.querySelector<HTMLElement>('[data-login-success]');
    if (!el) return;
    el.textContent = msg; el.classList.add('show');
  };
  const clearBanners = () => {
    document.querySelector<HTMLElement>('[data-login-error]')?.classList.remove('show');
    document.querySelector<HTMLElement>('[data-login-success]')?.classList.remove('show');
  };

  // On mount, check Firebase Auth currentUser and enable the button if we have one.
  // This handles the case where a user previously signed in but landed on the login
  // screen again (cached Auth session). It also means even if signInWithGoogle()
  // throws later, the button can still work using currentUser.
  getCurrentPendingUser().then((u) => {
    pendingUser = u;
    const btn = document.getElementById('request-access-btn') as HTMLButtonElement | null;
    setButton(btn, pendingUser);
    if (u) setSuccess('Signed in as ' + u.email + ' — if not yet on the access list, tap Request access below.');
  }).catch(() => {});

  const handleAfterSignIn = async (tenantId: string) => {
    const { signInWithGoogle, requestAccessFromGoogle, signOut: doSignOut, consumeInviteLink, applyInviteOnSignIn } = await import('../auth');

    // Invite-link path: #/invite/{code}
    const inviteMatch = location.hash.match(/#\/invite\/([A-Z0-9]+)/i);

    let result: Awaited<ReturnType<typeof signInWithGoogle>>;
    try {
      result = await signInWithGoogle(tenantId);
    } catch (err) {
      // Sign-in threw (e.g. permission-denied during post-signin Firestore read).
      // Even on failure the Firebase Auth credential may still be present — recover
      // currentUser and enable the Request Access button so the user has a way out.
      const u = await getCurrentPendingUser();
      if (u) {
        pendingUser = u;
        const btn = document.getElementById('request-access-btn') as HTMLButtonElement | null;
        setButton(btn, pendingUser);
        setSuccess('Signed in as ' + u.email + ' — something went wrong on our side, but you can still request access below.');
      }
      throw err;
    }

    if (inviteMatch && !('needsRequestAccess' in result)) {
      try {
        const token = await consumeInviteLink(tenantId, inviteMatch[1].toUpperCase());
        if (token) {
          const session = await applyInviteOnSignIn(tenantId, result.uid, result.email, result.name, token);
          setSuccess('Invite accepted — entering as ' + session.role + '…');
          setTimeout(() => { location.hash = '#/overview'; }, 600);
          return;
        } else {
          await doSignOut();
          setError('That invite link has expired or already been used. Ask your owner for a fresh one.');
          return;
        }
      } catch (e) {
        await doSignOut();
        setError(e instanceof Error ? e.message : 'Invite link could not be accepted.');
        return;
      }
    }

    if ('needsRequestAccess' in result) {
      pendingUser = { uid: result.uid, email: result.email, name: result.name };
      const btn = document.getElementById('request-access-btn') as HTMLButtonElement | null;
      setButton(btn, pendingUser);
      const signInBtn = document.getElementById('google-signin') as HTMLButtonElement | null;
      if (signInBtn) {
        const txt = signInBtn.querySelector<HTMLElement>('[data-google-btn-text]') || signInBtn;
        signInBtn.disabled = false;
        txt.textContent = 'Signed in as ' + result.email + ' — choose below';
      }
      setSuccess('Signed in as ' + result.email + '. You are not on the access list yet — tap Request access below.');
      return;
    }

    // Successful sign-in with a role
    location.hash = '#/overview';
  };

  document.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    const googleBtn = t.closest<HTMLButtonElement>('[data-action="google-signin"]');
    if (googleBtn) {
      const tId = (document.querySelector<HTMLSelectElement>('[data-login-tenant]')?.value) || 'techtatwa';
      document.body.dataset.tenant = tId;
      googleBtn.disabled = true;
      const txt = googleBtn.querySelector<HTMLElement>('[data-google-btn-text]') || googleBtn;
      const old = txt.textContent; txt.textContent = 'Opening Google…';
      clearBanners();
      try {
        await handleAfterSignIn(tId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Sign-in failed. Try again.';
        setError(msg);
        googleBtn.disabled = false;
        txt.textContent = old || 'Sign in with Google';
      }
      return;
    }

    const req = t.closest<HTMLButtonElement>('[data-action="request-access"]');
    if (req) {
      // If pendingUser isn't set yet (button was just rendered), recover from currentUser.
      if (!pendingUser) pendingUser = await getCurrentPendingUser();
      if (!pendingUser) {
        setError('Please sign in with Google first — then you can request access.');
        return;
      }
      const tId = (document.querySelector<HTMLSelectElement>('[data-login-tenant]')?.value) || 'techtatwa';
      req.disabled = true;
      const old = req.textContent;
      req.textContent = 'Sending…';
      try {
        const { requestAccessFromGoogle, signOut: doSignOut } = await import('../auth');
        await requestAccessFromGoogle(tId, pendingUser.uid, pendingUser.email, pendingUser.name, '');
        setSuccess('Request sent! The workspace owner has been notified. Try again later — once they approve you, sign in again to enter.');
        await doSignOut();
        pendingUser = null;
        req.disabled = true;
        req.textContent = 'Request sent — sit tight';
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not send request.');
        req.disabled = false;
        req.textContent = old || 'Request access';
      }
      return;
    }
  });

  document.addEventListener('change', (e) => {
    const sel = e.target as HTMLSelectElement;
    if (sel.matches('[data-login-tenant]')) document.body.dataset.tenant = sel.value;
  });
}
